/**
 * Pure helpers for the AI-reply "Sources" block. A source is a citation the
 * AI attached to its answer: an absolute article URL (external) or a widget
 * path like `/help/<slug>?locale=en` (internal help/news). See
 * `message-sources.tsx` for the rendered block.
 */

/** One AI-reply citation, as it arrives from the BE (SSE payload / history). */
export interface MessageSource {
  link?: string | null;
  seoTitle?: string | null;
  seoDescription?: string | null;
}

/**
 * External = an absolute `http(s)://` URL (opens in a new tab). Anything else
 * — a relative widget path (`/help/...`) or a bare KB reference — is internal.
 * Mirrors the BE contract: `link.startsWith('http')` → external.
 */
export function isExternalSource(link: string | null | undefined): boolean {
  return typeof link === "string" && /^https?:\/\//i.test(link.trim());
}

/**
 * Host of an external link, with a leading `www.` stripped, for the card's
 * domain label + favicon lookup. `null` for internal / unparseable links.
 */
export function sourceHost(link: string | null | undefined): string | null {
  if (!isExternalSource(link)) return null;
  try {
    // `link` is a non-null external URL per `isExternalSource`.
    return new URL((link as string).trim()).hostname.replace(/^www\./i, "") || null;
  } catch {
    return null;
  }
}

/**
 * Registrable ("root") domain of a host — `learn.wexio.io` → `wexio.io`.
 * Favicons are looked up on this rather than the full host: Google's service
 * indexes far more root domains than subdomains, and a docs subdomain almost
 * always shares the brand's favicon. Heuristic: last two labels, or last three
 * when the second-to-last is a short multi-part-TLD segment (`co.uk`,
 * `com.au`, …). Good enough for a favicon lookup; not a public-suffix parser.
 */
export function rootDomain(host: string | null | undefined): string | null {
  if (!host) return null;
  const parts = host.split(".").filter(Boolean);
  if (parts.length <= 2) return parts.join(".") || null;
  const secondLast = parts[parts.length - 2];
  const take = secondLast.length <= 3 ? 3 : 2;
  return parts.slice(-take).join(".");
}

/**
 * Favicon URL for a source's domain via Google's favicon service (returns a
 * generic globe for unknown hosts, so it never 404s to a broken image). Looks
 * up the registrable domain (`rootDomain`) so brand subdomains still resolve.
 * `null` for internal links — the caller renders its own in-widget icon.
 */
export function sourceFaviconUrl(link: string | null | undefined, size = 64): string | null {
  const domain = rootDomain(sourceHost(link));
  if (!domain) return null;
  return `https://www.google.com/s2/favicons?domain=${encodeURIComponent(domain)}&sz=${size}`;
}

/**
 * A source is renderable only if it has SOMETHING to show — at minimum a
 * title or a link. Fully empty rows (no link, no title) are dropped so the
 * block never renders a blank card. `seoTitle`/`seoDescription`/`link` may
 * each be null individually; the renderer shows only what's present.
 */
export function hasRenderableContent(source: MessageSource): boolean {
  return !!(source.seoTitle?.trim() || source.link?.trim());
}

/**
 * The card's title line: the explicit `seoTitle`, else the host (external),
 * else the raw link. Never empty when `hasRenderableContent` is true.
 */
export function sourceTitle(source: MessageSource): string {
  const title = source.seoTitle?.trim();
  if (title) return title;
  return sourceHost(source.link) ?? source.link?.trim() ?? "";
}
